import { z } from 'zod';
import { Money } from './money';
import { PromoRule } from './catalog';

/** CRUD меню из бэк-офиса. Удаления нет — только архив:
 *  товар в старых чеках должен оставаться находимым. */

export const UpsertCategory = z.object({
  name: z.string().min(1).max(60),
  station: z.enum(['kitchen', 'bar']).nullable().optional(),
  /** родительская группа: «Бар → Напитки». Два уровня и не больше */
  parentId: z.string().nullable().optional(),
  /** наценка группы, %; null — брать у родителя */
  markupPct: z.number().int().min(0).max(2000).nullable().optional(),
  /** можно ли давать скидку на товары группы (алкоголь часто нельзя) */
  allowDiscount: z.boolean().optional(),
  /** списывать ингредиенты при продаже; выключают для услуг */
  writeOffOnSale: z.boolean().optional(),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable().optional(),
  sort: z.number().int().optional(),
  isActive: z.boolean().optional(),
  hoursNote: z.string().max(200).nullable().optional(),
  foundedYear: z.number().int().min(1900).max(2100).nullable().optional(),
});
export type UpsertCategory = z.infer<typeof UpsertCategory>;

export const UpsertProduct = z.object({
  photoUrl: z.string().url().max(300).nullable().optional(),
  description: z.string().max(200).nullable().optional(),
  badges: z.string().max(40).nullable().optional(),
  categoryId: z.string().optional(),
  name: z.string().min(1).max(80).optional(),
  price: Money.optional(),
  unit: z.enum(['PIECE', 'KG', 'L', 'PORTION']).optional(),
  isActive: z.boolean().optional(),
  trackStock: z.boolean().optional(),

  /** артикул и штрихкод: магазину нужны, кафе не мешают */
  sku: z.string().max(40).nullable().optional(),
  barcode: z.string().max(40).nullable().optional(),

  /**
   * Минимальный остаток: ниже него позиция попадёт в «пора заказать».
   * null — не следим, и это нормальный выбор для половины меню
   */
  minStock: z.number().min(0).nullable().optional(),

  /** закупается у поставщика; выключено у заготовок — их делают сами */
  isPurchased: z.boolean().optional(),

  /** сырьё: живёт на складе и в техкартах, на кассе не продаётся */
  isIngredient: z.boolean().optional(),

  /** крепость для алкоголя, % об. */
  abv: z.number().min(0).max(100).nullable().optional(),
});
export type UpsertProduct = z.infer<typeof UpsertProduct>;

export const CreateProduct = UpsertProduct.required({ categoryId: true, name: true, price: true });
export type CreateProduct = z.infer<typeof CreateProduct>;

/** Стоп-лист: remaining=null — полный стоп, число — осталось порций,
 *  off=true — снять со стопа. */
export const StopSet = z.object({
  locationId: z.string(),
  productId: z.string(),
  remaining: z.number().int().min(0).nullable().optional(),
  off: z.boolean().optional(),
  /** снять автоматически в полночь; по умолчанию — да */
  untilEndOfDay: z.boolean().optional(),
});
export type StopSet = z.infer<typeof StopSet>;

export const LocationsList = z.array(z.object({
  id: z.string(),
  name: z.string(),
  note: z.string().nullable().optional(),
  isActive: z.boolean().optional(),
}));
export type LocationsList = z.infer<typeof LocationsList>;

// ─── Редактор модификаторов ───

export const UpsertModifierGroup = z.object({
  name: z.string().min(1).max(60),
  minSelect: z.number().int().min(0),
  maxSelect: z.number().int().min(1),
  isActive: z.boolean().optional(),
}).refine((g) => g.maxSelect >= g.minSelect, {
  message: 'maxSelect не может быть меньше minSelect',
});
export type UpsertModifierGroup = z.infer<typeof UpsertModifierGroup>;

export const UpsertModifierOption = z.object({
  name: z.string().min(1).max(60),
  priceDelta: Money.default(0),
  /** себестоимость добавки — уедет в снапшот продажи и фудкост */
  cost: Money.optional(),
  removesProductId: z.string().nullable().optional(),
  removesQty: z.number().positive().nullable().optional(),
  isActive: z.boolean().optional(),
});
export type UpsertModifierOption = z.infer<typeof UpsertModifierOption>;

/** Привязка группы к товару: on=false отвязывает. */
export const ProductGroupToggle = z.object({
  groupId: z.string(),
  on: z.boolean(),
});
export type ProductGroupToggle = z.infer<typeof ProductGroupToggle>;

export const AdminModifierGroups = z.array(z.object({
  id: z.string(),
  name: z.string(),
  minSelect: z.number().int(),
  maxSelect: z.number().int(),
  isActive: z.boolean(),
  options: z.array(z.object({
    id: z.string(),
    name: z.string(),
    priceDelta: Money,
    cost: Money,
    isActive: z.boolean(),
    /**
     * Что не списывать со склада при этой добавке.
     *
     * «Без лука» — значит лук не должен уйти со склада. Сервер это
     * отдавал, но договор молчал, и офис не знал, что связь уже
     * есть: экран показывал пустое поле там, где связь настроена.
     */
    removes: z.object({
      productId: z.string(),
      qty: z.number(),
    }).optional(),
  })),
}));
export type AdminModifierGroups = z.infer<typeof AdminModifierGroups>;

// ─── Акции ───

export const CreatePromo = z.object({
  name: z.string().min(1).max(60),
  rule: PromoRule,
});
export type CreatePromo = z.infer<typeof CreatePromo>;

export const PatchPromo = z.object({
  name: z.string().min(1).max(60).optional(),
  isActive: z.boolean().optional(),
});
export type PatchPromo = z.infer<typeof PatchPromo>;

export const AdminPromos = z.array(z.object({
  id: z.string(),
  name: z.string(),
  isActive: z.boolean(),
  rule: PromoRule,
}));
export type AdminPromos = z.infer<typeof AdminPromos>;

/** Импорт меню из CSV: быстрый старт для заведений с готовым меню. */
export const MenuImportRow = z.object({
  category: z.string().min(1).max(60),
  name: z.string().min(1).max(80),
  price: Money.min(0),
  unit: z.enum(['PIECE', 'KG', 'L', 'PORTION']).default('PIECE'),
});
export const MenuImport = z.object({
  rows: z.array(MenuImportRow).min(1).max(500),
});
export type MenuImport = z.infer<typeof MenuImport>;

export const MenuImportResult = z.object({
  createdCategories: z.number().int(),
  createdProducts: z.number().int(),
  skipped: z.number().int(),
});
export type MenuImportResult = z.infer<typeof MenuImportResult>;

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
/** Часы работы точки для гостевой витрины. */
export const PatchLocation = z.object({
  publicUrl: z.string().url().max(200).nullable().optional(),
  tipUrl: z.string().url().max(200).nullable().optional(),
  openTime: z.string().regex(HHMM).nullable().optional(),
  closeTime: z.string().regex(HHMM).nullable().optional(),
  phone: z.string().max(20).nullable().optional(),
  address: z.string().max(160).nullable().optional(),
  note: z.string().max(300).nullable().optional(),
  isActive: z.boolean().optional(),
});
export type PatchLocation = z.infer<typeof PatchLocation>;

/** Патч категории: базовые поля + архив с переносом позиций. */
export const PatchCategory = UpsertCategory.partial().extend({
  /** при isActive:false — куда перенести активные позиции; null/пусто — в архив вместе */
  moveProductsTo: z.string().nullable().optional(),
});
export type PatchCategory = z.infer<typeof PatchCategory>;
